import { LoaderCircle, ServerOff } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { AccessGate } from "./components/AccessGate";
import { GameViewport } from "./components/GameViewport";
import { ProfileCompletion } from "./components/ProfileCompletion";
import { ReturnBriefing } from "./components/ReturnBriefing";
import { ShellHeader } from "./components/ShellHeader";
import { TacticalPlanner } from "./components/TacticalPlanner";
import { useAuth } from "./auth/AuthProvider";
import {
  ApiError,
  deriveWebSocketBase,
  loadGuildCallProjections,
  VaelorApi,
} from "./lib/api";
import { normalizePublicPrestige } from "./lib/citadel-profile";
import type { RuntimeConfig } from "./lib/config";
import { newestGameSnapshot } from "./lib/game-snapshot";
import { dismissProfilePopoverFromOutsidePointer } from "./lib/profile-popover-pointer-dismiss";
import { isReturnBriefingEligible } from "./lib/return-briefing";
import { SessionEpoch, type SessionLease } from "./lib/session-epoch";

import { singleFlight } from "./lib/single-flight";
import type {
  AuthMeResponse,
  CrestPalette,
  CrestSigil,
  GuildCallProjection,
  GameSnapshot,
  GuildPrivateView,
  GuildRole,
  PlayerProfile,
  PlayerShowcase,
  PublicCitadel,
  PublicPrestige,
} from "./types";

const MAP_V5_BUILD_ENABLED = import.meta.env.VITE_MAP_V5_ENABLED === "1";
const PVP_BUILD_ENABLED = import.meta.env.VITE_PVP_ENABLED === "1";

const ChatPanel = lazy(() =>
  import("./components/ChatPanel").then(({ ChatPanel: component }) => ({ default: component }))
);
const GuildPanel = lazy(() =>
  import("./components/GuildPanel").then(({ GuildPanel: component }) => ({ default: component }))
);

function PanelLoading({ label }: { label: string }) {
  return <div className="notice" role="status" aria-live="polite">{label}</div>;
}

export function ServiceFault({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry: () => void;
}) {
  const headingId = useId();
  const messageId = useId();

  return (
    <main className="service-fault" aria-labelledby={headingId}>
      <ServerOff size={36} aria-hidden="true" />
      <h1 id={headingId}>{title}</h1>
      <p id={messageId} role="alert" aria-live="assertive" aria-atomic="true">{message}</p>
      <button
        className="primary-command"
        type="button"
        onClick={onRetry}
        aria-describedby={messageId}
        autoFocus
      >
        Réessayer
      </button>
    </main>
  );
}

async function readAuthoritativeProfile(api: VaelorApi): Promise<AuthMeResponse> {
  const profileResponse = await api.me();
  return profileResponse;
}

async function readAuthoritativeGuild(api: VaelorApi): Promise<GuildPrivateView> {
  const guildResponse = await api.guildMe();
  return guildResponse;
}

export type AppRuntimeAuth = ReturnType<typeof useAuth>;

export function App({ config }: { config: RuntimeConfig }) {
  const auth = useAuth();
  return <AppRuntime config={config} auth={auth} />;
}

export function AppRuntime({
  config,
  auth,
  api: injectedApi,
  initialChatOpen = true,
}: {
  config: RuntimeConfig;
  auth: AppRuntimeAuth;
  api?: VaelorApi;
  initialChatOpen?: boolean;
}) {
  const [profile, setProfile] = useState<PlayerProfile>();
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailPreferenceSaving, setEmailPreferenceSaving] = useState(false);
  const [profileError, setProfileError] = useState<string>();
  const [gameSnapshot, setGameSnapshot] = useState<GameSnapshot>();
  const [publicCitadels, setPublicCitadels] = useState<PublicCitadel[]>([]);
  const [selfPrestige, setSelfPrestige] = useState<PublicPrestige>({ points: 0, rank: null, version: 1, honors: [] });
  const [customPortraitUrl, setCustomPortraitUrl] = useState<string>();
  const [gameLoading, setGameLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(initialChatOpen);
  const [profileOpen, setProfileOpen] = useState(false);
  const [guildPanelOpen, setGuildPanelOpen] = useState(false);
  const [tacticalPlannerOpen, setTacticalPlannerOpen] = useState(false);
  const [heartFrontOpen, setHeartFrontOpen] = useState(false);
  const [embeddedChronicleOpen, setEmbeddedChronicleOpen] = useState(false);
  const [guildView, setGuildView] = useState<GuildPrivateView>();
  const [callProjection, setCallProjection] = useState<GuildCallProjection>({ incoming: [], outgoing: [] });
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState<string>();
  const [guildSyncPending, setGuildSyncPending] = useState(false);
  const [guildSyncError, setGuildSyncError] = useState<string>();
  const guildTriggerRef = useRef<HTMLButtonElement>(null);
  const tacticalPlannerTriggerRef = useRef<HTMLButtonElement>(null);
  const embeddedChronicleWasOpenRef = useRef(false);
  const activeProfileReaderRef = useRef<object | null>(null);
  const activePublicCitadelsReaderRef = useRef<object | null>(null);
  const sessionEpochRef = useRef(new SessionEpoch<VaelorApi>());
  const sessionEpoch = sessionEpochRef.current;
  const [authPending, setAuthPending] = useState<"google" | "signup" | "login">();
  const api = useMemo(() => {
    if (injectedApi) return injectedApi;
    let ownedApi: VaelorApi;
    ownedApi = new VaelorApi(
      config.apiBase,
      auth.getAccessToken,
      fetch,
      (error) => {
        if (sessionEpoch.invalidate(ownedApi)) {
          auth.invalidateSession(error.message);
        }
      },
    );
    return ownedApi;
  }, [auth.authenticated, auth.getAccessToken, auth.invalidateSession, config.apiBase, injectedApi, sessionEpoch]);
  sessionEpoch.sync(auth.authenticated, api);
  const captureSession = useCallback((): SessionLease<VaelorApi> => sessionEpoch.capture(api), [api, sessionEpoch]);
  const isCurrentSession = useCallback(
    (lease: SessionLease<VaelorApi>): boolean => sessionEpoch.isCurrent(lease),
    [sessionEpoch],
  );
  const webSocketBase = useMemo(() => deriveWebSocketBase(config.apiBase), [config.apiBase]);
  const acceptGameSnapshot = useCallback((snapshot: GameSnapshot) => {
    if (!sessionEpoch.isCurrent(sessionEpoch.capture(api))) return;
    setGameSnapshot((current) => newestGameSnapshot(current, snapshot));
  }, [api, sessionEpoch]);
  const acceptEmbeddedChronicleModal = useCallback((open: boolean) => {
    if (!sessionEpoch.isCurrent(sessionEpoch.capture(api))) return;
    setEmbeddedChronicleOpen(open);
    if (!open) return;
    setChatOpen(false);
    setProfileOpen(false);
    setGuildPanelOpen(false);
    setTacticalPlannerOpen(false);
    setHeartFrontOpen(false);
  }, [api, sessionEpoch]);
  useEffect(() => {
    const wasOpen = embeddedChronicleWasOpenRef.current;
    embeddedChronicleWasOpenRef.current = embeddedChronicleOpen;
    if (wasOpen && !embeddedChronicleOpen) {
      window.requestAnimationFrame(() => tacticalPlannerTriggerRef.current?.focus());
    }
  }, [embeddedChronicleOpen]);

  const profileReader = useMemo(() => {
    const lease = sessionEpoch.capture(api);
    const key = { api, epoch: lease.epoch };
    return {
      key,
      read: singleFlight(async () => {
        if (!sessionEpoch.isCurrent(lease)) return;
        setProfileLoading(true);
        setProfileError(undefined);
        setGuildSyncError(undefined);
        try {
          const response = await api.me();
          if (activeProfileReaderRef.current !== key || !sessionEpoch.isCurrent(lease)) return;
          setProfile(response.player);
          if (!response.player.guildId) setGuildView(undefined);
        } catch (cause) {
          if (activeProfileReaderRef.current !== key || !sessionEpoch.isCurrent(lease)) return;
          const message = cause instanceof Error ? cause.message : "Le profil joueur est indisponible.";
          const diagnostic = config.environment === "production" || !(cause instanceof ApiError)
            ? undefined
            : cause.diagnostic;
          setProfileError(diagnostic ? `${message} — diagnostic staging : ${diagnostic}` : message);
        } finally {
          if (activeProfileReaderRef.current === key && sessionEpoch.isCurrent(lease)) setProfileLoading(false);
        }
      }),
    };
  }, [api, auth.authenticated, config.environment, sessionEpoch]);
  const loadProfile = useCallback(() => {
    activeProfileReaderRef.current = profileReader.key;
    return profileReader.read();
  }, [profileReader]);

  useEffect(() => {
    if (auth.authenticated) {
      const { key } = profileReader;
      void loadProfile();
      return () => {
        if (activeProfileReaderRef.current === key) {
          activeProfileReaderRef.current = null;
        }
      };
    }
    activeProfileReaderRef.current = null;
    embeddedChronicleWasOpenRef.current = false;
    setProfileLoading(false);
    setProfileSaving(false);
    setEmailPreferenceSaving(false);
    setProfile(undefined);
    setProfileError(undefined);
    setGameSnapshot(undefined);
    setGameLoading(false);
    setChatOpen(false);
    setProfileOpen(false);
    setGuildPanelOpen(false);
    setTacticalPlannerOpen(false);
    setHeartFrontOpen(false);
    setEmbeddedChronicleOpen(false);
    setGuildView(undefined);
    setCallProjection({ incoming: [], outgoing: [] });
    setCallsLoading(false);
    setCallsError(undefined);
    setGuildSyncPending(false);
    setGuildSyncError(undefined);
    setPublicCitadels([]);
    setSelfPrestige({ points: 0, rank: null, version: 1, honors: [] });
    setAuthPending(undefined);
    setCustomPortraitUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return undefined;
    });
  }, [auth.authenticated, loadProfile, profileReader]);
  const loadGuildCalls = useCallback(async (role?: GuildRole): Promise<GuildCallProjection> => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    setCallsLoading(true);
    setCallsError(undefined);
    try {
      const next = await loadGuildCallProjections(api, role);
      sessionEpoch.assertCurrent(lease);
      setCallProjection(next);
      return next;
    } catch (cause) {
      if (isCurrentSession(lease)) {
        setCallsError(cause instanceof ApiError ? cause.message : "Les appels de ralliement sont indisponibles.");
      }
      throw cause;
    } finally {
      if (isCurrentSession(lease)) setCallsLoading(false);
    }
  }, [api, captureSession, isCurrentSession, sessionEpoch]);
  useEffect(() => {
    if (!auth.authenticated || !profile) return;
    void loadGuildCalls().catch(() => undefined);
  }, [auth.authenticated, loadGuildCalls, profile?.playerId]);
  const publicCitadelsReader = useMemo(() => {
    const lease = sessionEpoch.capture(api);
    const key = { playerId: profile?.playerId, epoch: lease.epoch };
    return {
      key,
      read: singleFlight(async () => {
        try {
          const response = await api.publicCitadels();
          if (activePublicCitadelsReaderRef.current !== key || !sessionEpoch.isCurrent(lease)) return;
          setPublicCitadels(response.citadels);
          setSelfPrestige(normalizePublicPrestige(response.selfPrestige));
        } catch {
          if (activePublicCitadelsReaderRef.current !== key || !sessionEpoch.isCurrent(lease)) return;
          setPublicCitadels([]);
          setSelfPrestige({ points: 0, rank: null, version: 1, honors: [] });
        }
      }),
    };
  }, [api, profile?.playerId, sessionEpoch]);
  useEffect(() => {
    const playerId = profile?.playerId;
    if (!auth.authenticated || !playerId || publicCitadelsReader.key.playerId !== playerId) return;
    const { key } = publicCitadelsReader;
    activePublicCitadelsReaderRef.current = key;
    void publicCitadelsReader.read();
    return () => {
      if (activePublicCitadelsReaderRef.current === key) {
        activePublicCitadelsReaderRef.current = null;
      }
    };
  }, [auth.authenticated, profile?.playerId, publicCitadelsReader]);
  const loadGuildView = useCallback(async () => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    const response = await api.guildMe();
    sessionEpoch.assertCurrent(lease);
    setGuildView(response);
    return response;
  }, [api, captureSession, sessionEpoch]);
  const reloadProfileAndGuildAndCalls = useCallback(async () => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    setGuildSyncPending(true);
    setGuildSyncError(undefined);
    let profileResponse: AuthMeResponse;
    try {
      profileResponse = await readAuthoritativeProfile(api);
      sessionEpoch.assertCurrent(lease);
    } catch (cause) {
      if (!isCurrentSession(lease)) throw cause;
      const message = cause instanceof ApiError
        ? cause.message
        : "Le profil et la Compagnie n’ont pas pu être resynchronisés.";
      setGuildView(undefined);
      setCallProjection({ incoming: [], outgoing: [] });
      setCallsError(undefined);
      setGuildSyncError(message);
      setGuildSyncPending(false);
      throw cause;
    }

    setProfile(profileResponse.player);
    if (!profileResponse.player.guildId) {
      setGuildView(undefined);
      setCallProjection({ incoming: [], outgoing: [] });
      setCallsError(undefined);
      setGuildPanelOpen(false);
      setChatOpen(false);
      setGuildSyncPending(false);
      return { profile: profileResponse.player, calls: { incoming: [], outgoing: [] } };
    }

    let guildResponse: GuildPrivateView;
    try {
      guildResponse = await readAuthoritativeGuild(api);
      sessionEpoch.assertCurrent(lease);
    } catch (cause) {
      if (!isCurrentSession(lease)) throw cause;
      const message = cause instanceof ApiError
        ? cause.message
        : "La Compagnie n’a pas pu être resynchronisée.";
      setGuildView(undefined);
      setCallProjection({ incoming: [], outgoing: [] });
      setCallsError(undefined);
      setGuildSyncError(message);
      setGuildSyncPending(false);
      throw cause;
    }

    setGuildView(guildResponse);

    // Profile + private Compagnie are the authoritative post-mutation state.
    // Calls are a secondary projection: they must not hide the converged chat.
    setGuildSyncPending(false);
    const currentRole = guildResponse.members.find(
      (member) => member.playerId === profileResponse.player.playerId,
    )?.role;
    try {
      const nextCalls = await loadGuildCalls(currentRole);
      sessionEpoch.assertCurrent(lease);
      setCallsError(undefined);
      return { profile: profileResponse.player, guild: guildResponse, calls: nextCalls };
    } catch (cause) {
      if (!isCurrentSession(lease)) throw cause;
      const callsMessage = cause instanceof ApiError
        ? cause.message
        : "La liste des appels de ralliement n’a pas pu être actualisée. Réessaie.";
      setCallsError(callsMessage);
      return { profile: profileResponse.player, guild: guildResponse, callsError: callsMessage };
    }
  }, [api, captureSession, isCurrentSession, loadGuildCalls, sessionEpoch]);
  const reloadProfileAndGuild = useCallback(async () => {
    const result = await reloadProfileAndGuildAndCalls();
    if (!result.guild) throw new Error("La Compagnie n’a pas pu être relue après sa fondation.");
    return { profile: result.profile, guild: result.guild };
  }, [reloadProfileAndGuildAndCalls]);
  const openGuildPanel = () => {
    setProfileOpen(false);
    setGuildView(undefined);
    setCallsError(undefined);
    setGuildPanelOpen(true);
  };
  useEffect(() => {
    if (!auth.authenticated || !profile?.showcase?.customPortraitAvailable) {
      setCustomPortraitUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return undefined;
      });
      return;
    }
    let active = true;
    const lease = sessionEpoch.capture(api);
    void api.customShowcaseImage().then((blob) => {
      if (!active || !sessionEpoch.isCurrent(lease)) return;
      const next = URL.createObjectURL(blob);
      setCustomPortraitUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return next;
      });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [api, auth.authenticated, profile?.showcase?.customPortraitAvailable, profile?.showcase?.customPortraitStatus, sessionEpoch]);
  useEffect(() => {
    if (!auth.authenticated || !profile) {
      setGameSnapshot(undefined);
      setGameLoading(false);
      return;
    }
    let active = true;
    const lease = sessionEpoch.capture(api);
    const refresh = singleFlight(async (initial: boolean) => {
      if (initial && sessionEpoch.isCurrent(lease)) setGameLoading(true);
      try {
        const response = await api.gameBootstrap();
        if (active && sessionEpoch.isCurrent(lease)) acceptGameSnapshot(response.snapshot);
      } catch (cause) {
        if (active && initial && sessionEpoch.isCurrent(lease)) {
          setProfileError(cause instanceof Error ? cause.message : "L’état du Bastion est indisponible.");
        }
      } finally {
        if (active && initial && sessionEpoch.isCurrent(lease)) setGameLoading(false);
      }
    });
    void refresh(true);
    const interval = window.setInterval(() => { void refresh(false); }, 5_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [acceptGameSnapshot, api, auth.authenticated, profile, sessionEpoch]);
  const saveProfile = async (displayName: string, reengagementEnabled: boolean) => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    setProfileSaving(true);
    setProfileError(undefined);
    try {
      const response = await api.updateProfile(displayName.trim(), reengagementEnabled);
      sessionEpoch.assertCurrent(lease);
      setProfile(response.player);
    } catch (cause) {
      if (isCurrentSession(lease)) {
        setProfileError(cause instanceof Error ? cause.message : "Le pseudonyme n’a pas pu être confirmé.");
      }
    } finally {
      if (isCurrentSession(lease)) setProfileSaving(false);
    }
  };

  const updateEmailPreference = async (enabled: boolean) => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    setEmailPreferenceSaving(true);
    setProfileError(undefined);
    try {
      const response = await api.updateEmailPreferences(enabled);
      sessionEpoch.assertCurrent(lease);
      setProfile((current) => current ? {
        ...current,
        emailPreferences: response.emailPreferences,
      } : current);
    } catch (cause) {
      if (isCurrentSession(lease)) {
        setProfileError(cause instanceof Error ? cause.message : "Le choix de relance n'a pas pu être enregistré.");
      }
    } finally {
      if (isCurrentSession(lease)) setEmailPreferenceSaving(false);
    }
  };
  const saveCrest = async (sigil: CrestSigil, palette: CrestPalette) => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    const response = await api.updateCrest(sigil, palette);
    sessionEpoch.assertCurrent(lease);
    setProfile((current) => current ? { ...current, crest: response.crest } : current);
    return response.crest;
  };
  const saveShowcase = async (
    portrait: PlayerShowcase["portrait"],
    frame: PlayerShowcase["frame"],
    nameColor: PlayerShowcase["nameColor"],
  ) => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    const response = await api.updateShowcase(portrait, frame, nameColor);
    sessionEpoch.assertCurrent(lease);
    setProfile((current) => current ? { ...current, showcase: response.showcase } : current);
    return response.showcase;
  };
  const uploadShowcaseImage = async (image: Blob) => {
    const lease = captureSession();
    sessionEpoch.assertCurrent(lease);
    const upload = await api.uploadShowcaseImage(image);
    sessionEpoch.assertCurrent(lease);
    const persisted = await api.customShowcaseImage();
    sessionEpoch.assertCurrent(lease);
    const nextUrl = URL.createObjectURL(persisted);
    setCustomPortraitUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return nextUrl;
    });
    setProfile((current) => current ? {
      ...current,
      showcase: {
        ...(current.showcase ?? { portrait: "herald", frame: "iron", nameColor: "ivory", animated: false }),
        customPortraitAvailable: true,
        customPortraitStatus: upload.status,
      },
    } : current);
    return upload;
  };
  const launchAuth = (
    action: "google" | "signup" | "login",
    start: () => Promise<void>,
  ) => {
    setAuthPending(action);
    void start().catch(() => setAuthPending(undefined));
  };
  const login = () => launchAuth("login", auth.login);
  const signup = () => launchAuth("signup", auth.signup);
  const loginWithGoogle = () => launchAuth("google", auth.loginWithGoogle);
  const logout = () => {
    sessionEpoch.invalidate(api);
    auth.invalidateSession(null);
    void auth.logout().catch(() => undefined);
  };

  if (!auth.ready) {
    return <div className="full-status"><LoaderCircle className="spin" /><strong>Ouverture du sceau d’identité…</strong></div>;
  }

  return (
    <div
      className={`player-shell ${auth.authenticated && chatOpen && profile ? "player-shell--chat-open" : ""}`}
      onPointerDownCapture={(event) => dismissProfilePopoverFromOutsidePointer(
        profileOpen,
        event.target,
        () => setProfileOpen(false),
      )}
    >
      {auth.authenticated && profileOpen && !embeddedChronicleOpen && <div className="profile-popover-dismiss-layer" aria-hidden="true" />}
      <ShellHeader
        authenticated={auth.authenticated}
        authReady={auth.ready}
        authAvailable={auth.available}
        profile={auth.authenticated ? profile : undefined}
        guildTriggerRef={guildTriggerRef}
        chatOpen={auth.authenticated && chatOpen}
        profileOpen={auth.authenticated && profileOpen}
        onLogin={login}
        onLogout={logout}
         onToggleChat={() => setChatOpen((open) => !open)}
         onToggleProfile={() => setProfileOpen((open) => !open)}
         onOpenGuild={openGuildPanel}
         incomingCallCount={auth.authenticated ? callProjection.incoming.filter((call) => call.status === "active").length : 0}
         emailPreferenceSaving={auth.authenticated && emailPreferenceSaving}
         profileError={auth.authenticated ? profileError : undefined}
         onEmailPreferenceChange={(enabled) => void updateEmailPreference(enabled)}
         modalInert={auth.authenticated && embeddedChronicleOpen}
      />

      {!auth.authenticated ? (
        <AccessGate
          onLogin={login}
          onSignup={signup}
          onGoogle={loginWithGoogle}
          authAvailable={auth.available}
          error={auth.error}
          pendingAction={authPending}
        />
      ) : profileLoading ? (
        <div className="full-status"><LoaderCircle className="spin" /><strong>Rattachement au monde…</strong></div>
      ) : !profile ? (
        <ServiceFault
          title="Le monde ne répond pas."
          message={profileError || "Le portail n’a reçu aucun profil joueur."}
          onRetry={() => void loadProfile()}
        />
      ) : (
        <>
          {gameLoading ? (
            <div className="full-status"><LoaderCircle className="spin" /><strong>Synchronisation du Bastion…</strong></div>
          ) : gameSnapshot ? (
              <GameViewport
                gameUrl={config.gameUrl}
                webSocketBase={webSocketBase}
                api={api}
                {...(MAP_V5_BUILD_ENABLED ? {
                  mapV5Enabled: true,
                  mapV5ProfileIdentity: {
                    accountId: profile.accountId,
                    playerId: profile.playerId,
                    worldId: profile.worldId,
                  },
                } : {})}
                {...(MAP_V5_BUILD_ENABLED || PVP_BUILD_ENABLED ? { captureSession, isCurrentSession } : {})}
                snapshot={gameSnapshot}
                displayName={profile.displayName}
                bestRing={profile.bestRing}
                crest={profile.crest ?? null}
                showcase={profile.showcase ?? { portrait: "herald", frame: "iron", nameColor: "ivory", animated: false }}
                prestige={selfPrestige}
                publicCitadels={publicCitadels}
                onSaveCrest={saveCrest}
                onSaveShowcase={saveShowcase}
                 customPortraitUrl={customPortraitUrl}
                 onUploadShowcaseImage={uploadShowcaseImage}
                 onOpenPrestige={() => setChatOpen(false)}
                  onSnapshot={acceptGameSnapshot}
                  heartFrontOpen={heartFrontOpen}
                  onHeartFrontOpenChange={setHeartFrontOpen}
                  embeddedChronicleOpen={embeddedChronicleOpen}
                  onEmbeddedChronicleOpenChange={acceptEmbeddedChronicleModal}
              />
          ) : (
            <ServiceFault
              title="Le Bastion ne répond pas."
              message={profileError || "Le jeu attend son état serveur."}
              onRetry={() => void loadProfile()}
            />
          )}
          {gameSnapshot && !heartFrontOpen && !embeddedChronicleOpen && (
            <section className="tactical-planner-launcher" aria-label="Table de Commandement">
              <p className="eyebrow">Décision privée</p>
              <h2>Organiser les prochains chantiers</h2>
              <p>Prépare un ordre de construction sans réserver de ressources ni lancer de chantier.</p>
              <button
                ref={tacticalPlannerTriggerRef}
                className="secondary-command"
                type="button"
                onClick={() => setTacticalPlannerOpen(true)}
                aria-expanded={tacticalPlannerOpen}
              >
                Ouvrir le Planificateur du Bastion
              </button>
            </section>
          )}
          {gameSnapshot && !embeddedChronicleOpen && isReturnBriefingEligible(gameSnapshot) && (
            <ReturnBriefing api={api} snapshot={gameSnapshot} />
          )}
          {chatOpen && !embeddedChronicleOpen && !guildSyncPending && !guildSyncError && (
            <Suspense fallback={<PanelLoading label="Ouverture du canal…" />}>
              <ChatPanel
                api={api}
                webSocketBase={webSocketBase}
                profile={profile}
                onClose={() => setChatOpen(false)}
              />
            </Suspense>
          )}
          {!embeddedChronicleOpen && !profile.profileComplete && (
            <ProfileCompletion
              initialName={profile.displayName}
              saving={profileSaving}
              error={profileError}
              onSave={saveProfile}
            />
          )}
        </>
      )}
      {auth.authenticated && guildPanelOpen && !embeddedChronicleOpen && profile && (
        <Suspense fallback={<PanelLoading label="Ouverture de la Compagnie…" />}>
          <GuildPanel
            api={api}
            profile={profile}
           guild={guildView}
           onLoadGuild={loadGuildView}
           onCreated={reloadProfileAndGuild}
           calls={callProjection}
           callsLoading={callsLoading}
           callsError={callsError}
           onLoadCalls={loadGuildCalls}
            onCallAccepted={reloadProfileAndGuildAndCalls}
            onMembershipChanged={reloadProfileAndGuildAndCalls}
            onClose={() => setGuildPanelOpen(false)}
          returnFocusRef={guildTriggerRef}
        />
        </Suspense>
      )}
      {auth.authenticated && tacticalPlannerOpen && !embeddedChronicleOpen && profile && (
        <TacticalPlanner
          api={api}
          onClose={() => {
            setTacticalPlannerOpen(false);
            window.requestAnimationFrame(() => tacticalPlannerTriggerRef.current?.focus());
          }}
        />
      )}
    </div>
  );
}
